import React, { FunctionComponent, useState } from 'react';
import {
  Paper,
  MenuItem,
  Button,
  useTheme,
  Menu,
  Grid,
  GridSpacing,
  Container,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslationConfig } from './useTranslationConfig';
import { resolveFlagCode } from './flagOverrides';

interface LocalizationSelectorProps {
  hasHeader?: boolean;
  onLanguageChange?: (langCode: string) => void;
}

const LocalizationSelector: FunctionComponent<LocalizationSelectorProps> = ({
  hasHeader = false,
  onLanguageChange,
}) => {
  const theme = useTheme();
  const {
    translationConfig,
    availableLanguages,
    selectedLanguage,
    setSelectedLanguage,
  } = useTranslationConfig();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (langCode: string) => {
    setSelectedLanguage(langCode);
    onLanguageChange?.(langCode);
    handleClose();
  };

  const currentSelection = availableLanguages.find(
    (lang) => lang.code === selectedLanguage
  );

  if (!translationConfig?.translationEnabled) return null;

  return (
    <Container>
      <Grid
        container
        sx={{
          position: 'relative',
          zIndex: 10,
          my: hasHeader ? '8px' : '4px',
          justifyContent: 'flex-end',
        }}
        spacing={theme.spacing() as GridSpacing}
      >
        <Paper style={{ borderRadius: 8 }} elevation={0}>
          <Button
            size="small"
            variant="text"
            onClick={handleOpen}
            sx={{
              color: theme.palette.text.primary,
              fontSize: 'small',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              py: 0.5,
              paddingLeft: 0.5,
              paddingRight: 0,
            }}
            data-testid="localization-selector-button"
          >
            <LanguageIcon />
            {currentSelection && (
              <>
                {translationConfig.translationFlagsEnabled && (
                  <span
                    className={`fi fi-${resolveFlagCode(
                      currentSelection.countryCode
                    )}`}
                  />
                )}
                <span style={{ fontWeight: 'bold', minWidth: 20 }}>
                  {currentSelection.code.toUpperCase()}
                </span>
              </>
            )}
            <KeyboardArrowDownIcon />
          </Button>
        </Paper>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableScrollLock
          slotProps={{
            paper: {
              sx: {
                minWidth: 100,
              },
            },
          }}
          data-testid="localization-selector-menu"
        >
          {availableLanguages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              data-testid={`localization-selector-menu-item-${lang.code}`}
            >
              {translationConfig.translationFlagsEnabled && (
                <span
                  className={`fi fi-${resolveFlagCode(lang.countryCode)}`}
                  style={{ marginRight: 8, fontSize: 'small' }}
                />
              )}
              {lang.label}
            </MenuItem>
          ))}
        </Menu>
      </Grid>
    </Container>
  );
};

export default LocalizationSelector;
